import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, Send, Plus, Mail, Clock, CheckCircle } from "lucide-react";
import { useMessageStats } from "@/hooks/use-messagerie";

// Import des composants de messagerie (à créer)
import MessagesReceivedList from "@/components/messagerie/MessagesReceivedList";
import MessagesSentList from "@/components/messagerie/MessagesSentList";
import CreateMessageForm from "@/components/messagerie/CreateMessageForm";
import MessageDetail from "@/components/messagerie/MessageDetail";

const MessagingPage = () => {
  const [activeTab, setActiveTab] = useState("received");
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: stats, isLoading: statsLoading } = useMessageStats();

  // Gestion de la sélection d'un message
  const handleMessageSelect = (messageId) => {
    setSelectedMessageId(messageId);
    setShowCreateForm(false);
  };

  // Gestion de l'affichage du formulaire de création
  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    setSelectedMessageId(null);
  };

  // Gestion du retour à la liste
  const handleBackToList = () => {
    setSelectedMessageId(null);
    setShowCreateForm(false);
  };

  // Rendu conditionnel pour le détail d'un message
  if (selectedMessageId) {
    return (
      <MessageDetail messageId={selectedMessageId} onBack={handleBackToList} />
    );
  }

  // Rendu conditionnel pour le formulaire de création
  if (showCreateForm) {
    return (
      <CreateMessageForm
        onBack={handleBackToList}
        onSuccess={handleBackToList}
      />
    );
  }

  return (
    <div className="mx-auto p-6 space-y-6">
      {/* En-tête avec statistiques */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Messagerie Interne
          </h1>
          <p className="text-muted-foreground">
            Gérez votre communication interne avec l'équipe
          </p>
        </div>

        <Button onClick={handleShowCreateForm} className="w-fit">
          <Plus className="mr-2 h-4 w-4" />
          Nouveau message
        </Button>
      </div>

      {/* Cartes de statistiques */}
      {!statsLoading && stats?.data && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Messages reçus
              </CardTitle>
              <Inbox className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.data.totalReceived}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Messages envoyés
              </CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.totalSent}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Non lus</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.data.unreadMessages}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">En attente</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.data.pendingMessages}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation par onglets */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="received" className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Boîte de réception
            {!statsLoading && stats?.data?.unreadMessages > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs">
                {stats.data.unreadMessages}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Messages envoyés
          </TabsTrigger>
        </TabsList>

        {/* Contenu des onglets */}
        <TabsContent value="received" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="h-5 w-5" />
                Messages reçus
              </CardTitle>
              <CardDescription>
                Consultez et gérez les messages qui vous ont été envoyés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MessagesReceivedList onMessageSelect={handleMessageSelect} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Messages envoyés
              </CardTitle>
              <CardDescription>
                Consultez les messages que vous avez envoyés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MessagesSentList onMessageSelect={handleMessageSelect} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessagingPage;
